/**
 * LE TOTAL DE LA NUIT — LA FEUILLE DE CAISSE, SANS NAVIGATEUR.
 *
 * ═══ UNE SEULE RÈGLE, DEUX ÉCRANS (15/10/2026) ═══
 *
 * Ce module vivait dans le navigateur (`night-sheet-model.ts`), qui l'importe
 * désormais d'ici. Il a rejoint le cœur pour que les Soirées affichent la
 * « Caisse de la nuit » — Rapports, Compté, CA arrêté — avec EXACTEMENT les
 * chiffres du bandeau de la fiche de nuit : le serveur les calcule ici, sur
 * les mêmes réponses que la fiche lit, et n'envoie que le résultat. Une copie
 * de la formule côté Soirées aurait fini par contredire la fiche.
 *
 * D'où deux contraintes, que rien d'autre ne garantit :
 *   · AUCUN import : ni base, ni Node, ni React. Le navigateur compile ce
 *     fichier tel quel (Vite, chemin relatif) ; un `import` du cœur y tirerait
 *     la base de données.
 *   · Les entrées sont des formes STRUCTURELLES minimales (`ClotureLue`,
 *     `NuitLue`) : les types de l'écran et les réponses du serveur y entrent
 *     sans conversion.
 *
 * Le modèle est celui du classeur que l'exploitante remplit depuis toujours
 * (« CAISSE OCTOBRE 26 », nuit du 02/10/2026) : pour chaque caisse, ce que le
 * terrain a compté — terminaux d'un côté, espèces par coupure de l'autre — et
 * ce que le rapport annonce (« Z = ») ; puis le récapitulatif : Entrée,
 * Vestiaire, chaque salle, TOTAL BAR, Total Service, Total Z, Total,
 * Différence ; et le récap des espèces, coupure par coupure, tous postes
 * confondus. Dans Gestion : l'Entrée est l'Accueil (le guichet Shotgun), les
 * salles sont les points de vente « bar » (leurs noms viennent du serveur,
 * jamais d'ici), Z est la SOURCE de la clôture, le compté est le CONSTATÉ.
 *
 * Ce fichier est pur — ni React, ni réseau, ni formatage : les tests le
 * nourrissent des chiffres du classeur et doivent retrouver, au centime, ses
 * 91 € de différence sur les bars et ses 114 € sur la nuit.
 *
 * ═══ HORS OFFERTS, TOUJOURS ═══
 *
 * La ligne OFFERT (carte serveur) n'est pas une recette : elle n'entre ni
 * dans le rapport, ni dans le compté, ni dans l'écart. Elle est rendue à part,
 * pour mémoire. (`totals.source_cents` du serveur, lui, la compte : on ne s'en
 * sert pas.)
 *
 * ═══ L'ÉCART SE LIT AU POINT DE VENTE ═══
 *
 * Le classeur fait « TOTAL − Z » caisse par caisse, et c'est ce qu'il faut :
 * une salle dont le rapport n'annonce aucune espèce mais où l'on a compté
 * 915 € de billets (une salle, le 02/10) a un écart de 8 €, pas de − 907 €. Un écart
 * ligne à ligne (carte contre carte) inventerait ce second chiffre. Un point
 * de vente n'a donc d'écart que s'il est compté EN ENTIER — chaque moyen que
 * le rapport annonce a son constaté — et que son rapport existe.
 *
 * ═══ LES ESPÈCES : LA LIGNE D'ABORD, LE COMPTAGE À DÉFAUT ═══
 *
 * Le constaté de la ligne « Espèces » est ce que la clôture confronte et
 * retient ; quand il n'est pas saisi mais que des coupures sont comptées (ou
 * un total d'espèces forcé), c'est ce comptage qui fait foi — exactement la
 * règle du serveur pour proposer les écarts (`computeDiscrepancyProposals`).
 * Sans elle, les 915 € de la salle qui n'a pas de ligne « Espèces »
 * disparaîtraient du compté.
 *
 * ═══ COMPTÉ N'EST PAS ENCORE REPORTÉ (revue du 07/10/2026) ═══
 *
 * Ce que le comptage fait foi pour la FEUILLE (le classeur ne connaît que
 * lui), la finalisation ne le sait pas : `closeClosing` n'additionne que les
 * LIGNES. Des espèces comptées que rien ne porte sur une ligne « Espèces »
 * (pas encore reportées, ou une salle dont le rapport n'a pas de ligne
 * Espèces) sortiraient de l'arrêté — 915 € au Bar ROOM 2 le 02/10. La ligne
 * le dit donc (`especesAReporter`), la feuille le compte (`aReporter`), le
 * rail ne coche pas le point de vente, et le serveur refuse de finaliser
 * (`obstaclesFinalisation`, closing.ts) : la MÊME règle des deux côtés.
 *
 * ═══ CE QUI N'EST PAS COMPTÉ NE COMPTE PAS ═══
 *
 * Un point de vente pas encore compté a un compté « — », jamais 0, et pas
 * d'écart : un terminal pas encore saisi n'est pas un manque de 4 918 €.
 * L'écart total additionne les seuls écarts connus et DIT sur combien de
 * points de vente il porte. Un point de vente fermé, ou dont on n'a pas dit
 * s'il était ouvert, reste visible mais hors des totaux — et l'écran le dit.
 * « Tout est compté » exige qu'il n'en reste AUCUN sans réponse : sinon le
 * chiffre d'affaires « compté » omettrait en silence une salle entière.
 *
 * ═══ LES VENTES DU GUICHET D'UNE AUTRE NUIT (08/10/2026) ═══
 *
 * Le « rapport » de l'Accueil est son ATTENDU : la source corrigée des ventes
 * décidées (`expected_cents`), le guichet de la nuit plus les rattachements
 * sans clôture. Sous la ligne, chaque cas qui la touche se lit
 * (`horsNuit`) : « dont 1 060 € vendus sur la soirée du 26/09, rattachés à
 * cette nuit », ou « en attente de décision » — l'écart de l'Accueil dit
 * alors d'où il vient, sans ouvrir aucune autre page.
 *
 * ═══ UN RAPPORT QUI MANQUE N'EST PAS UN ZÉRO ═══
 *
 * Avant l'arrivée du mail ZAP (46 h après la nuit, le 02/10), les bars n'ont
 * aucun rapport : additionner l'Accueil seul et l'appeler « Rapport » ou
 * « chiffre d'affaires » ferait valoir zéro à tous les bars. Le total d'après
 * les rapports vaut donc « — » tant qu'un bar ou le vestiaire ouvert attend
 * son rapport (`rapportAttendu`), tant qu'aucune caisse ZAP n'est lue
 * (`zapAttendu`), ou tant que le guichet de l'Accueil n'est pas lisible
 * (droit de lire la billetterie) : une part cachée emporte le total, comme
 * pour les préventes côté serveur.
 */

/* ═══════════════════════ les entrées (formes minimales) ═══════════════════════ */

/** Un montant tel qu'il arrive : nombre, chaîne d'un `bigint` PostgreSQL, ou absent. */
export type CentsLus = number | string | null | undefined
type Cents = CentsLus

/** `SANS_OBJET` : les billets ne sont plus hors nuit (soirée redatée après la décision). */
export type StatutHorsNuitLu = 'EN_ATTENTE' | 'RATTACHE' | 'MAINTENU' | 'SANS_OBJET'

/** Un cas de ventes du guichet d'une autre nuit, vu d'une nuit (`off_night_sales`, `off_night.cases`). */
export interface CasHorsNuitLu {
  key: string
  side: 'VENTE' | 'SOIREE'
  other_business_date: string
  tickets: number
  total_cents: number | null
  status: StatutHorsNuitLu
  decision: { decision: 'RATTACHER' | 'MAINTENIR'; total_cents: number | null } | null
}

/** Les préventes en ligne d'une nuit (`GET /finance/nights/:date` → `preventes`). */
export interface PreventesLues {
  ttc_cents: number | null
  /** Absent d'un serveur d'avant le reversement : « non servi », jamais deviné. */
  payout_cents?: number | null
  source: 'REDDITION' | 'BILLETS' | 'REDDITION_ET_BILLETS' | 'SANS_BILLET' | null
  tickets: number
  reddition_refs: string[]
  provisional: boolean
  notes: string[]
}

/**
 * La billetterie des associations (Pretix) d'une nuit (`GET /finance/nights/:date`
 * → `associations`, 16/10/2026). `revenue_cents` : ce qui entre au chiffre
 * d'affaires — `null` quand le réglage est éteint OU que les droits le
 * cachent ; `revenue_enabled` dit lequel.
 */
export interface AssociationsLues {
  revenue_enabled: boolean
  revenue_cents: number | null
  sold: number
}

/** La fiche d'une nuit (`GET /finance/nights/:date`), ce que la feuille en lit. */
export interface NuitLue {
  soirees: Array<{ status: string }>
  by_point_of_sale: Array<{
    point_of_sale_id: string | null; name: string; kind: string | null; resolved: boolean
    announced_cents: Cents; offers_cents: Cents
  }>
  accueil: { onsite: { facial_cents: number } | null }
  /** `history` : la provenance d'une nuit reprise de l'historique (`estReprise`) ; absente sinon. */
  validation: { revenue_cents: number | null; history?: unknown } | null
  /** Absent d'un serveur d'avant le 08/10/2026 : « aucun cas ». */
  off_night_sales?: CasHorsNuitLu[]
  /** Absent d'un serveur d'avant octobre 2026 : « non servi », jamais 0. */
  preventes?: PreventesLues | null
  /** Absent d'un serveur d'avant le 16/10/2026 : aucune billetterie d'association au chiffre. */
  associations?: AssociationsLues | null
}

/** Une ligne de périmètre de la clôture (`GET /finance/closings/:id` → `pos[]`). */
export interface PointDeVenteLu {
  id: string
  point_of_sale_name: string
  point_of_sale_kind: string | null
  location_name: string | null
  was_open: boolean | null
  cash_counts: Array<{ denomination_cents: number; quantity: number }>
  cash_total_override_cents: Cents
  cash_effective_cents: Cents
  lines: Array<{
    payment_method: string; payment_method_label: string
    source_cents: Cents; expected_cents?: Cents; observed_cents: Cents
  }>
}

/** La clôture d'une nuit (`GET /finance/closings/:id`), ce que la feuille en lit. */
export interface ClotureLue {
  status: string
  pos: PointDeVenteLu[]
  components: Array<{ component_code: string; amount_cents: Cents }>
  presales?: { frozen?: boolean; payout_cents?: number | null } | null
  off_night?: { cases: CasHorsNuitLu[] } | null
}

// Les noms d'origine du module, gardés pour le corps (inchangé depuis le navigateur).
type ClosingDetail = ClotureLue
type ClosingPosView = PointDeVenteLu
type NightDetailResponse = NuitLue
type NuitAvecPreventes = NuitLue
type OffNightSaleRow = CasHorsNuitLu
type StatutHorsNuit = StatutHorsNuitLu
type PreventesNuit = PreventesLues

/**
 * LA DÉCISION D'UN CAS S'APPLIQUE-T-ELLE ? Une décision, et pas « sans objet ».
 * Le STATUT ne suffit pas : des billets vendus après la décision remettent le
 * cas « en attente », et le serveur applique toujours la décision à SES
 * billets (`transfertsAccueil`) — l'écran qui filtrait sur RATTACHÉ / MAINTENU
 * l'oubliait (revue du 08/10/2026, GHN-5). Le panneau du guichet hors nuit
 * (navigateur) la réimporte d'ici.
 */
export function decisionEnVigueur(c: Pick<CasHorsNuitLu, 'decision' | 'status'>): boolean {
  return c.decision !== null && c.status !== 'SANS_OBJET'
}

/** La composante des préventes en ligne que la finalisation écrit (11/10/2026, `closeClosing`). */
export const COMPOSANTE_PREVENTES = 'TICKETING_ONLINE'

/**
 * La composante de la billetterie des associations (16/10/2026,
 * `billetterie-associations.ts`). Recopiée ici : ce module reste sans import.
 * Comme les préventes, elle n'est PAS du sur-place — aucune caisse de la nuit
 * ne l'a comptée (l'argent passe par Stripe) ; avant ce traitement explicite,
 * tout code autre que `TICKETING_ONLINE` tombait dans le sur-place.
 */
export const COMPOSANTE_ASSOCIATIONS = 'TICKETING_ASSOCIATIONS'

/* ═══════════════════════ la forme rendue ═══════════════════════ */

/** Le rang d'un point de vente dans la feuille : l'ordre du classeur. */
export type GenreFeuille = 'ACCUEIL' | 'VESTIAIRE' | 'BAR' | 'AUTRE'

export type EtatLigne =
  /** Chaque moyen annoncé a son constaté : l'écart se calcule. */
  | 'compte'
  /** Une partie seulement : le compté se lit, l'écart attend. */
  | 'partiel'
  /** Rien de compté. */
  | 'a-compter'
  /** Déclaré fermé cette nuit : hors des totaux. */
  | 'ferme'
  /** Ouvert ou fermé : pas encore dit. Hors des totaux, et l'écran le dit. */
  | 'sans-reponse'
  /** Pas de clôture : la feuille ne connaît que le rapport. */
  | 'rapport-seul'

/** Un cas de ventes du guichet hors nuit, sous la ligne de l'Accueil. Le composant écrit la phrase. */
export interface HorsNuitFeuille {
  cle: string
  /** `entrant` : vendus pendant cette nuit sur une autre soirée ; `sortant` : la soirée d'ici, vendue pendant une autre nuit. */
  sens: 'entrant' | 'sortant'
  /** L'autre nuit (AAAA-MM-JJ). */
  nuit: string
  billets: number
  /** `null` = montant non lisible (droit de la billetterie). */
  cents: number | null
  statut: StatutHorsNuit
  /** Rattaché : il est DANS le rapport de cette ligne (entrant) ou en est sorti (sortant). */
  dansLeRapport: boolean
}

export interface LigneFeuille {
  /** La ligne de périmètre de la clôture, ou une clé du rapport sans clôture. */
  cle: string
  /** La ligne de périmètre (pour ouvrir l'étape du point de vente), `null` sans clôture. */
  closingPosId: string | null
  nom: string
  lieu: string | null
  genre: GenreFeuille
  etat: EtatLigne
  /** Compté : terminaux (tickets TPE). */
  carte: number | null
  /** Compté : espèces (ligne « Espèces », ou comptage par coupures à défaut). */
  especes: number | null
  /** Compté : tout autre moyen (chèque, virement…), jamais fondu dans la carte. */
  autres: number | null
  compte: number | null
  /** Ce que le rapport annonce, hors offerts (Z). */
  rapport: number | null
  /** Compté − rapport, seulement si la ligne est comptée en entier et son rapport connu. */
  ecart: number | null
  /** Les moyens annoncés qui attendent encore leur comptage (libellés du référentiel). */
  manquants: string[]
  /** Les espèces viennent du comptage par coupures : la ligne « Espèces » n'a pas de constaté. */
  especesDuComptage: boolean
  /**
   * Ces espèces comptées ne sont portées par AUCUNE ligne « Espèces » : la
   * finalisation ne les retiendrait pas. Faux quand il n'y a rien à reporter
   * (aucune ligne Espèces et 0 € compté). La règle du serveur, recopiée :
   * `obstaclesFinalisation` (closing.ts).
   */
  especesAReporter: boolean
  /**
   * Le rapport de ce point de vente devrait exister et ne se lit pas encore :
   * un bar ou le vestiaire ouvert sans source, ou le guichet de l'Accueil
   * masqué. Tant qu'une ligne ouverte l'attend, le total « rapport » vaut « — ».
   */
  rapportAttendu: boolean
  /** Offerts annoncés (carte serveur) : une information, jamais une recette. */
  offerts: number | null
  /** Sans clôture : une caisse du rapport sans point de vente de Gestion. */
  aRattacher: boolean
  /** L'Accueil seulement : les ventes du guichet d'une autre nuit qui le touchent. */
  horsNuit: HorsNuitFeuille[]
}

export interface Agregat {
  compte: number | null
  rapport: number | null
  /** Somme des écarts CONNUS ; `null` tant qu'aucun ne l'est. */
  ecart: number | null
  /** Points de vente dont l'écart est connu. */
  comptes: number
  /** Points de vente ouverts dans l'agrégat. */
  total: number
}

export interface CoupureFeuille { valeur: number; quantite: number; montant: number }

export interface FeuilleCaisse {
  avecCloture: boolean
  /** La clôture est finalisée (ou remplacée) : son arrêté existe. */
  figee: boolean
  /** Toutes les lignes, dans l'ordre du classeur : Accueil, vestiaire, bars, le reste. */
  lignes: LigneFeuille[]
  accueil: Agregat
  vestiaire: Agregat
  bars: Agregat
  /** « Total Service » du classeur : bars + vestiaire. */
  service: Agregat
  /** Les points de vente d'un autre genre (tabac…), hors service et hors Accueil. */
  autres: Agregat
  /** Toute la nuit sur place : Accueil, vestiaire, bars et le reste. */
  total: Agregat
  /** « ESPÈCE / CB / CHÈQUE » du classeur : le compté par moyen, tous postes. */
  moyens: { carte: number | null; especes: number | null; autres: number | null }
  /** Offerts annoncés sur la nuit (hors recette), `null` s'il n'y en a pas de connu. */
  offerts: number | null
  preventes: {
    /** Faux = le serveur ne sert pas encore les préventes (ou la nuit n'est pas lue). */
    servies: boolean
    /**
     * Ce que Shotgun reverse au club : la part du chiffre d'affaires
     * (décision du 11/10/2026, la méthode de la DAF). `null` = inconnu.
     */
    reversees: number | null
    /** Ce que les acheteurs ont payé, prix + frais de service — un repère, hors chiffre d'affaires. */
    ttc: number | null
    source: PreventesNuit['source']
    billets: number | null
    refs: string[]
    provisoire: boolean
    notes: string[]
  }
  /**
   * LA BILLETTERIE DES ASSOCIATIONS au chiffre de la nuit (16/10/2026).
   * `dansLeChiffre` : le réglage la fait entrer (fiche) ou la clôture l'a
   * arrêtée. `direct` : ce que la fiche lit maintenant (`null` = cachée par
   * les droits) ; `arretee` : la composante d'une clôture finalisée.
   */
  associations: {
    servies: boolean
    dansLeChiffre: boolean
    direct: number | null
    /** Elle entre au chiffre mais les droits cachent son montant : l'écran dit « réservé », jamais « — ». */
    cachee: boolean
    arretee: number | null
    billets: number
  }
  /** Le chiffre d'affaires de la nuit : sur place + préventes (+ billetterie des associations, réglage allumé). */
  ca: {
    /** Rapport sur place + préventes. */
    rapport: number | null
    /** Compté sur place + préventes — seulement quand TOUT est compté. */
    compte: number | null
    /**
     * L'ARRÊTÉ d'une clôture finalisée (composantes hors offerts, le retenu
     * primant sur le constaté) + préventes ; `null` avant la finalisation.
     * Les préventes sont celles que la finalisation a FIGÉES (composante
     * « Billetterie en ligne », 11/10/2026) ; une clôture finalisée avant,
     * sans elles, prend les préventes lues en direct — jamais les deux.
     */
    arrete: number | null
  }
  /** L'arrêté sur place seul (composantes hors offerts ET hors préventes) ; `null` avant la finalisation. */
  arreteSurPlace: number | null
  /**
   * Les préventes reversées que la finalisation a figées ; `null` = rien de
   * figé (pas finalisée, ou finalisée avant le 11/10/2026 sans rattrapage :
   * l'arrêté prend alors `preventes.reversees`, lu en direct).
   */
  preventesArretees: number | null
  /** Chaque point de vente ouvert est compté EN ENTIER, et aucun n'est sans réponse. */
  complet: boolean
  /** Points de vente ouverts dont les espèces comptées ne sont reportées sur aucune ligne. */
  aReporter: number
  /** Points de vente ouverts dont le rapport est attendu (voir `rapportAttendu`). */
  rapportsAttendus: number
  /** Sans clôture : aucune caisse ZAP lue pour la nuit — les bars n'ont pas encore de rapport. */
  zapAttendu: boolean
  /**
   * Sans clôture : une nuit REPRISE DE L'HISTORIQUE (16/10/2026). Son chiffre
   * est le « Total » du suivi de la DAF ; ses caisses (rapports ZAP,
   * comptage) n'ont jamais été dans Gestion et n'y arriveront pas. Rien n'y
   * est donc « attendu » : `zapAttendu` est faux, et les phrases le disent.
   * Faux dès qu'une caisse ZAP est lue : la nuit a alors ses rapports.
   */
  reprise: boolean
  /** Sans clôture : une soirée maintenue, mais le guichet de l'Accueil ne se lit pas. */
  accueilMasque: boolean
  /** Le récap des espèces, coupure par coupure, tous postes confondus. */
  especes: {
    coupures: CoupureFeuille[]
    /** Somme des coupures ; `null` si aucune n'est comptée. */
    totalCoupures: number | null
    /** Les totaux saisis directement, sans coupures (motif obligatoire au comptage). */
    saisiesDirectes: Array<{ nom: string; montant: number }>
    /** Les espèces de la nuit : coupures + saisies directes ; `null` si rien. */
    total: number | null
  }
  sansReponse: number
  fermes: number
}

/* ═══════════════════════ l'arithmétique ═══════════════════════ */

const MOYEN_CARTE = 'CB'
const MOYEN_ESPECES = 'ESPECES'
const MOYEN_OFFERT = 'OFFERT'

const enNombre = (v: Cents): number | null => {
  if (v === null || v === undefined) return null
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) ? n : null
}

/** Somme qui rend `null` tant qu'AUCUNE valeur n'est connue : un manque n'est pas un zéro. */
function somme(valeurs: Array<number | null>): number | null {
  let t = 0
  let connue = false
  for (const v of valeurs) {
    if (v === null) continue
    t += v
    connue = true
  }
  return connue ? t : null
}

function genreDe(kind: string | null): GenreFeuille {
  if (kind === 'BILLETTERIE') return 'ACCUEIL'
  if (kind === 'VESTIAIRE') return 'VESTIAIRE'
  if (kind === 'BAR') return 'BAR'
  return 'AUTRE'
}

const RANG: Record<GenreFeuille, number> = { ACCUEIL: 0, VESTIAIRE: 1, BAR: 2, AUTRE: 3 }

/** L'ordre du classeur, puis le nom — « Bar ROOM 2 » avant « Bar ROOM 10 ». */
function ranger(lignes: LigneFeuille[]): LigneFeuille[] {
  return [...lignes].sort((a, b) => RANG[a.genre] - RANG[b.genre]
    || a.nom.localeCompare(b.nom, 'fr', { numeric: true, sensitivity: 'base' }))
}

const OUVERTE = (l: LigneFeuille): boolean => l.etat === 'compte' || l.etat === 'partiel' || l.etat === 'a-compter' || l.etat === 'rapport-seul'

function agreger(lignes: LigneFeuille[]): Agregat {
  const ouvertes = lignes.filter(OUVERTE)
  return {
    compte: somme(ouvertes.map((l) => l.compte)),
    // Une part attendue emporte le total : jamais un zéro implicite.
    rapport: ouvertes.some((l) => l.rapportAttendu) ? null : somme(ouvertes.map((l) => l.rapport)),
    ecart: somme(ouvertes.map((l) => l.ecart)),
    comptes: ouvertes.filter((l) => l.ecart !== null).length,
    total: ouvertes.length,
  }
}

/* ═══════════════════════ une ligne de clôture ═══════════════════════ */

/** Le comptage d'espèces a-t-il eu lieu (coupures, ou total saisi directement) ? */
const comptageFait = (p: ClosingPosView): boolean =>
  enNombre(p.cash_total_override_cents) !== null || p.cash_counts.some((c) => c.quantity > 0)

/** L'attendu d'une ligne : la source corrigée des ventes d'une autre nuit décidées (la règle du rail). */
const attendu = (l: { source_cents: Cents; expected_cents?: Cents }): number | null =>
  enNombre(l.expected_cents !== undefined ? l.expected_cents : l.source_cents)

/** Les cas d'une nuit, sous la ligne de l'Accueil. */
export function horsNuitDeLaFeuille(cas: OffNightSaleRow[]): HorsNuitFeuille[] {
  // Un cas sans objet (soirée redatée depuis la décision) ne touche plus la caisse.
  return cas.filter((c) => c.status !== 'SANS_OBJET').map((c) => ({
    cle: c.key,
    sens: c.side === 'VENTE' ? 'entrant' as const : 'sortant' as const,
    nuit: c.other_business_date,
    billets: c.tickets,
    cents: c.total_cents,
    statut: c.status,
    dansLeRapport: c.side === 'VENTE' ? c.status === 'RATTACHE' : c.status !== 'RATTACHE',
  }))
}

export function ligneDeCloture(p: ClosingPosView, cas: OffNightSaleRow[] = []): LigneFeuille {
  const base = {
    cle: p.id, closingPosId: p.id, nom: p.point_of_sale_name, lieu: p.location_name || null,
    genre: genreDe(p.point_of_sale_kind), aRattacher: false,
    horsNuit: genreDe(p.point_of_sale_kind) === 'ACCUEIL' ? horsNuitDeLaFeuille(cas) : [],
  }
  const recette = p.lines.filter((l) => l.payment_method !== MOYEN_OFFERT)
  const offerts = somme(p.lines.filter((l) => l.payment_method === MOYEN_OFFERT).map((l) => enNombre(l.source_cents)))
  const rapport = somme(recette.map(attendu))

  if (p.was_open === false || p.was_open === null) {
    return {
      ...base, etat: p.was_open === false ? 'ferme' : 'sans-reponse',
      carte: null, especes: null, autres: null, compte: null, rapport, ecart: null,
      manquants: [], especesDuComptage: false, especesAReporter: false, rapportAttendu: false, offerts,
    }
  }

  const constate = (ls: typeof recette): number | null => somme(ls.map((l) => enNombre(l.observed_cents)))
  const lignesCarte = recette.filter((l) => l.payment_method === MOYEN_CARTE)
  const lignesEspeces = recette.filter((l) => l.payment_method === MOYEN_ESPECES)
  const lignesAutres = recette.filter((l) => l.payment_method !== MOYEN_CARTE && l.payment_method !== MOYEN_ESPECES)

  const carte = constate(lignesCarte)
  const especesSaisies = constate(lignesEspeces)
  const especesDuComptage = especesSaisies === null && comptageFait(p)
  const especes = especesSaisies ?? (especesDuComptage ? enNombre(p.cash_effective_cents) : null)
  const autres = constate(lignesAutres)
  const compte = somme([carte, especes, autres])

  // Ce que le rapport annonce et que personne n'a encore constaté.
  const manquants: string[] = []
  for (const l of recette) {
    if (attendu(l) === null || enNombre(l.observed_cents) !== null) continue
    if (l.payment_method === MOYEN_ESPECES && especes !== null) continue
    if (!manquants.includes(l.payment_method_label)) manquants.push(l.payment_method_label)
  }

  const etat: EtatLigne = compte === null ? 'a-compter' : manquants.length > 0 ? 'partiel' : 'compte'
  // À reporter : le comptage dit quelque chose qu'aucune ligne ne porte. Sans
  // ligne Espèces et à 0 € compté, l'arrêté et le comptage disent déjà pareil.
  const especesAReporter = especesDuComptage && (lignesEspeces.length > 0 || (especes ?? 0) !== 0)
  return {
    ...base, etat, carte, especes, autres, compte, rapport,
    ecart: etat === 'compte' && rapport !== null && compte !== null ? compte - rapport : null,
    manquants, especesDuComptage, especesAReporter, offerts,
    // Le bar et le vestiaire tiennent leur rapport de ZAP : sans lui, il est ATTENDU.
    rapportAttendu: rapport === null && (base.genre === 'BAR' || base.genre === 'VESTIAIRE'),
  }
}

/* ═══════════════════════ une nuit sans clôture : le rapport seul ═══════════════════════ */

function lignesDuRapport(n: NightDetailResponse): LigneFeuille[] {
  const vide = {
    closingPosId: null, lieu: null, etat: 'rapport-seul' as const,
    carte: null, especes: null, autres: null, compte: null, ecart: null,
    manquants: [], especesDuComptage: false, especesAReporter: false, rapportAttendu: false,
  }
  const lignes: LigneFeuille[] = n.by_point_of_sale.map((p) => ({
    ...vide,
    cle: p.point_of_sale_id ?? `alias:${p.name}`,
    nom: p.name,
    genre: p.resolved ? genreDe(p.kind) : 'AUTRE',
    rapport: enNombre(p.announced_cents),
    offerts: enNombre(p.offers_cents) === 0 ? null : enNombre(p.offers_cents),
    aRattacher: !p.resolved,
    horsNuit: [],
  }))
  // L'Accueil : le guichet Shotgun. Il n'apparaît que s'il a vendu, ou si une
  // soirée maintenue devait le faire vendre. Le serveur sert TOUJOURS le
  // guichet d'une nuit à soirée maintenue (à zéro s'il n'a rien vendu,
  // `onsiteSalesByMethod`) : `null` y veut dire MASQUÉ (sans le droit de lire
  // la billetterie, `finance-nights.ts`). Le montant reste « — », et il est
  // ATTENDU : le total d'après les rapports ne se fait pas sans lui.
  //
  // Le guichet ne compte que les ventes faites PENDANT la nuit ; l'attendu y
  // ajoute ce que les décisions y mettent — les ventes faites ici sur une
  // autre soirée et RATTACHÉES, la soirée d'ici vendue ailleurs et MAINTENUE.
  const o = n.accueil.onsite
  const maintenue = n.soirees.some((s) => s.status === 'SCHEDULED')
  const cas = n.off_night_sales ?? []
  if (o !== null || maintenue || cas.length > 0) {
    // Les montants FIGÉS à la décision, comme le serveur (`transfertsAccueil`) :
    // un billet remboursé depuis ne fait pas bouger ce qu'un humain a décidé.
    // La décision EN VIGUEUR, pas le statut : des billets vendus depuis
    // remettent le cas « en attente », et la décision vaut toujours pour les
    // siens. Le guichet de la nuit ne contient aucune vente d'une autre nuit :
    // rattachées ici ou maintenues sur la soirée d'ici, elles s'y ajoutent ;
    // la soirée d'ici rattachée ailleurs n'en retire rien.
    const ajoutes = cas.filter((c) => decisionEnVigueur(c)
      && ((c.side === 'VENTE' && c.decision!.decision === 'RATTACHER') || (c.side === 'SOIREE' && c.decision!.decision === 'MAINTENIR')))
    const montant = (c: OffNightSaleRow): number | null => c.decision?.total_cents ?? null
    const inconnu = ajoutes.some((c) => montant(c) === null)
    lignes.push({
      ...vide, cle: 'accueil', nom: 'Accueil', genre: 'ACCUEIL',
      rapport: o === null || inconnu ? null : o.facial_cents + ajoutes.reduce((t, c) => t + (montant(c) ?? 0), 0),
      offerts: null, aRattacher: false,
      rapportAttendu: o === null,
      horsNuit: horsNuitDeLaFeuille(cas),
    })
  }
  return lignes
}

/* ═══════════════════════ la feuille ═══════════════════════ */

function preventesDe(n: NuitAvecPreventes | null): FeuilleCaisse['preventes'] {
  const p = n?.preventes ?? null
  if (!p) return { servies: false, reversees: null, ttc: null, source: null, billets: null, refs: [], provisoire: false, notes: [] }
  // Un serveur d'avant le reversement ne le sert pas : elle n'est pas
  // devinée depuis le TTC, la feuille la dit « non servie ».
  if (p.payout_cents === undefined) return { servies: false, reversees: null, ttc: p.ttc_cents, source: p.source, billets: p.tickets, refs: p.reddition_refs, provisoire: p.provisional, notes: p.notes }
  return {
    servies: true, reversees: p.payout_cents, ttc: p.ttc_cents, source: p.source, billets: p.tickets,
    refs: p.reddition_refs, provisoire: p.provisional, notes: p.notes,
  }
}

function recapEspeces(d: ClosingDetail): FeuilleCaisse['especes'] {
  const parValeur = new Map<number, number>()
  const saisiesDirectes: Array<{ nom: string; montant: number }> = []
  let total: number | null = null
  for (const p of d.pos) {
    if (p.was_open !== true) continue
    const force = enNombre(p.cash_total_override_cents)
    if (force !== null) {
      // Un total saisi directement n'a pas de détail par coupure : il est dit à part.
      saisiesDirectes.push({ nom: p.point_of_sale_name, montant: force })
      total = (total ?? 0) + force
      continue
    }
    for (const c of p.cash_counts) {
      if (c.quantity <= 0) continue
      parValeur.set(c.denomination_cents, (parValeur.get(c.denomination_cents) ?? 0) + c.quantity)
      total = (total ?? 0) + c.denomination_cents * c.quantity
    }
  }
  const coupures = [...parValeur.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([valeur, quantite]) => ({ valeur, quantite, montant: valeur * quantite }))
  return {
    coupures,
    totalCoupures: coupures.length === 0 ? null : coupures.reduce((t, c) => t + c.montant, 0),
    saisiesDirectes,
    total,
  }
}

/**
 * La feuille de caisse d'une nuit : depuis la clôture quand elle existe (le
 * compté et le rapport), sinon depuis la fiche de nuit (le rapport seul). La
 * fiche de nuit apporte aussi les préventes ; sans elle, elles sont « non
 * servies ».
 */
export function feuilleDeCaisse(cloture: ClosingDetail | null, nuit: NuitAvecPreventes | null): FeuilleCaisse {
  const cas = cloture?.off_night?.cases ?? nuit?.off_night_sales ?? []
  const lignes = ranger(cloture ? cloture.pos.map((p) => ligneDeCloture(p, cas)) : nuit ? lignesDuRapport(nuit) : [])
  const de = (...g: GenreFeuille[]) => lignes.filter((l) => g.includes(l.genre))
  const ouvertes = lignes.filter(OUVERTE)
  const preventes = preventesDe(nuit)
  const sansReponse = lignes.filter((l) => l.etat === 'sans-reponse').length

  // Sans clôture et sans aucune caisse ZAP lue, les bars n'ont même pas de
  // ligne : l'Accueil seul n'est pas « le rapport » de la nuit.
  const sansZap = cloture === null && nuit !== null && nuit.by_point_of_sale.length === 0 && lignes.length > 0
  // Une nuit reprise n'ATTEND rien : son rapport ZAP ne viendra jamais (avant
  // le 16/10/2026, la fiche d'une nuit de 2017 disait « pas encore arrivé »).
  // Le total d'après les rapports reste « — » pour la même raison qu'au-dessus.
  // `?.` : une fiche lue sans validation (absente, pas seulement `null`) n'est pas reprise.
  // SEULEMENT sans caisse ZAP lue : la reprise a aussi validé des nuits
  // récentes (septembre 2026) dont les rapports ZAP SONT dans Gestion — leur
  // dire « pas dans Gestion » sous un tableau qui les liste se contredit, et
  // leur clôture reste à faire comme celle d'une nuit validée à la main.
  const reprise = cloture === null && nuit !== null && nuit.by_point_of_sale.length === 0
    && estReprise(nuit.validation?.history)
  const zapAttendu = sansZap && !reprise
  const brut = agreger(lignes)
  const total: Agregat = sansZap ? { ...brut, rapport: null } : brut

  // Tout est compté : chaque point de vente ouvert l'est EN ENTIER, et il
  // n'en reste aucun dont on ignore s'il était ouvert.
  const toutCompte = cloture !== null && ouvertes.length > 0 && sansReponse === 0 && ouvertes.every((l) => l.etat === 'compte')
  const plus = (a: number | null, b: number | null): number | null => (a === null || b === null ? null : a + b)

  // L'arrêté : les composantes que la finalisation a écrites (le retenu
  // prime, `closeClosing`), offerts exclus. Avant la finalisation, rien.
  // DEPUIS LE 11/10/2026, la finalisation écrit aussi les préventes
  // reversées (`TICKETING_ONLINE`) : elles sortent du sur-place et
  // remplacent les préventes lues en direct — les ajouter aux deux
  // compterait la billetterie en ligne deux fois.
  const figee = cloture !== null && (cloture.status === 'CLOSED' || cloture.status === 'SUPERSEDED')
  const composantes = figee ? cloture.components.filter((c) => c.component_code !== 'OFFERS') : []
  // LA BILLETTERIE DES ASSOCIATIONS (16/10/2026) sort aussi du sur-place,
  // EXPLICITEMENT : arrêtée, elle compte une fois, par sa composante.
  const surPlace = composantes.filter((c) => c.component_code !== COMPOSANTE_PREVENTES && c.component_code !== COMPOSANTE_ASSOCIATIONS)
  const enLigne = composantes.filter((c) => c.component_code === COMPOSANTE_PREVENTES)
  const desAssociations = composantes.filter((c) => c.component_code === COMPOSANTE_ASSOCIATIONS)
  const associationsArretees = !figee ? null : (somme(desAssociations.map((c) => enNombre(c.amount_cents))) ?? 0)
  // En direct (fiche) : réglage éteint = rien au chiffre ; allumé mais montant
  // caché par les droits = une part cachée, qui emporte le total. Une nuit
  // SANS billet d'association n'a rien à ajouter ni à cacher : allumer le
  // réglage ne change pas une nuit Shotgun seule (revue du 16/10 — elle
  // gagnait une ligne « 0,00 € » et perdait son total sans `finance.live.read`).
  const a = nuit?.associations ?? null
  const presentes = a !== null && a.revenue_enabled && a.sold > 0
  const associationsDirect: number | null = !presentes ? 0 : enNombre(a.revenue_cents)
  // ARRÊTÉE, la nuit compte la composante écrite — jamais la lecture en
  // direct : la ligne affichée, le rapport, le compté et l'arrêté disent le
  // même montant même si un réglage ou une part change ensuite (revue du 16/10).
  const associationsAuTotal: number | null = figee ? (associationsArretees ?? 0) : associationsDirect
  const plusAssociations = (x: number | null): number | null => (x === null || associationsAuTotal === null ? null : x + associationsAuTotal)
  const arreteSurPlace = composantes.length === 0 ? null : (somme(surPlace.map((c) => enNombre(c.amount_cents))) ?? 0)
  const preventesArretees = !figee ? null
    : enLigne.length > 0 ? somme(enLigne.map((c) => enNombre(c.amount_cents)))
      // Figées à zéro (aucune prévente, pas de composante) : le gel le dit.
      : cloture.presales?.frozen === true ? enNombre(cloture.presales.payout_cents) : null

  return {
    avecCloture: cloture !== null,
    figee,
    lignes,
    accueil: agreger(de('ACCUEIL')),
    vestiaire: agreger(de('VESTIAIRE')),
    bars: agreger(de('BAR')),
    service: agreger(de('BAR', 'VESTIAIRE')),
    autres: agreger(de('AUTRE')),
    total,
    moyens: {
      carte: somme(ouvertes.map((l) => l.carte)),
      especes: somme(ouvertes.map((l) => l.especes)),
      autres: somme(ouvertes.map((l) => l.autres)),
    },
    offerts: somme(ouvertes.map((l) => l.offerts)),
    preventes,
    associations: {
      servies: a !== null,
      dansLeChiffre: figee ? (associationsArretees ?? 0) !== 0 : presentes,
      direct: presentes ? enNombre(a.revenue_cents) : null,
      cachee: !figee && presentes && enNombre(a.revenue_cents) === null,
      arretee: associationsArretees,
      billets: a?.sold ?? 0,
    },
    ca: {
      rapport: plusAssociations(plus(total.rapport, preventes.reversees)),
      compte: toutCompte ? plusAssociations(plus(total.compte, preventes.reversees)) : null,
      // Arrêté : la composante écrite, jamais la lecture en direct.
      arrete: (() => {
        const x = plus(arreteSurPlace, preventesArretees ?? preventes.reversees)
        return x === null ? null : x + (associationsArretees ?? 0)
      })(),
    },
    arreteSurPlace,
    preventesArretees,
    complet: toutCompte,
    aReporter: ouvertes.filter((l) => l.especesAReporter).length,
    rapportsAttendus: reprise ? 0 : ouvertes.filter((l) => l.rapportAttendu).length,
    zapAttendu,
    reprise,
    accueilMasque: cloture === null && ouvertes.some((l) => l.genre === 'ACCUEIL' && l.rapportAttendu),
    especes: cloture ? recapEspeces(cloture) : { coupures: [], totalCoupures: null, saisiesDirectes: [], total: null },
    sansReponse,
    fermes: lignes.filter((l) => l.etat === 'ferme').length,
  }
}

/* ═══════════════════════ les phrases ═══════════════════════ */

function pluriel(n: number, un: string, plusieurs: string): string {
  return `${n} ${n > 1 ? plusieurs : un}`
}

/**
 * Sur quoi porte un écart : « sur 4 points de vente comptés sur 5 », ou
 * « sur les 5 points de vente » quand tout est compté. `null` sans écart.
 */
export function porteeEcart(a: Agregat): string | null {
  if (a.total === 0) return null
  if (a.comptes === 0) return 'Aucun point de vente compté en entier pour l’instant'
  if (a.comptes === a.total) {
    return a.total === 1 ? 'Sur le seul point de vente ouvert' : `Sur les ${a.total} points de vente`
  }
  return `Sur ${pluriel(a.comptes, 'point de vente compté', 'points de vente comptés')} sur ${a.total}`
}

/** Ce que dit une ligne sous son nom : son état, en clair. `null` = rien à dire. */
export function detailLigne(l: LigneFeuille): string | null {
  switch (l.etat) {
    case 'ferme': return 'Fermé cette nuit'
    case 'sans-reponse': return 'Ouvert ou fermé ? Pas encore indiqué, donc hors du total'
    case 'a-compter':
      if (l.rapport !== null) return 'Pas encore compté'
      return l.rapportAttendu ? 'Pas encore de rapport, rien de compté' : 'Rien d’annoncé, rien de compté'
    case 'partiel': return `Compté en partie : il manque ${l.manquants.join(', ').toLowerCase()}`
    case 'rapport-seul':
      if (l.aRattacher) return 'Caisse du rapport à rattacher'
      return l.rapportAttendu ? 'Guichet non lu : il demande le droit de lire la billetterie' : null
    case 'compte':
      if (l.especesAReporter) return 'Espèces comptées, pas encore reportées sur la ligne Espèces'
      if (l.rapport === null) return l.rapportAttendu ? 'Compté, mais pas encore de rapport' : 'Aucun rapport pour ce point de vente'
      return l.especesDuComptage ? 'Espèces : comptage par coupures' : null
  }
}

/* ═══════════════════════ les phrases des bandeaux (assistant et fiche de nuit) ═══════════════════════ */

/** « , hors 1 point de vente sans réponse » — ou rien. */
function horsSansReponse(f: FeuilleCaisse): string {
  return f.sansReponse === 0 ? '' : `, hors ${pluriel(f.sansReponse, 'point de vente', 'points de vente')} sans réponse`
}

/**
 * L'aide du « Compté » d'un bandeau — UNE phrase pour l'assistant et la fiche
 * de nuit. « Compté en entier » se compte sur l'état des lignes, pas sur
 * l'écart : un point de vente compté sans rapport l'est aussi. Le compté
 * additionne les comptages partiels : l'aide ne dit donc jamais « 0 compté »
 * sous un montant de 17 976 € (revue du 07/10/2026).
 */
/**
 * LA PHRASE D'UNE NUIT REPRISE, partout où une nuit sans clôture dirait
 * « pas encore arrivé » (fiche, bandeau, caisse de la nuit des Soirées) : la
 * conséquence d'abord — ce détail n'est pas dans Gestion —, pas une attente.
 */
export const REPRISE_SANS_DETAIL = 'Nuit reprise de l’historique : le détail des caisses (rapports ZAP, comptage) n’est pas dans Gestion'

export function aideCompte(f: FeuilleCaisse): string {
  if (!f.avecCloture) return f.reprise ? REPRISE_SANS_DETAIL : 'Se remplit avec la clôture'
  if (f.total.compte === null) return `Rien de compté pour l’instant${horsSansReponse(f)}`
  if (f.complet) {
    return f.aReporter === 0
      ? 'Espèces et terminaux comptés'
      : `Tout est compté ; espèces à reporter sur ${pluriel(f.aReporter, 'point de vente', 'points de vente')}`
  }
  const entiers = f.lignes.filter((l) => OUVERTE(l) && l.etat === 'compte').length
  return `Comptage en cours : ${pluriel(entiers, 'point de vente', 'points de vente')} sur ${f.total.total} compté${entiers > 1 ? 's' : ''} en entier${horsSansReponse(f)}`
}

/** L'aide de l'« Écart » d'un bandeau : sa portée, et ce qui reste hors du total. */
export function aideEcart(f: FeuilleCaisse): string {
  if (f.total.ecart === null) return `Se calcule dès qu’un point de vente est compté en entier${horsSansReponse(f)}`
  return `${porteeEcart(f.total) ?? 'Compté moins rapport'}${horsSansReponse(f)}`
}

/** L'aide du « Rapport » (ou de l'« Annoncé ») : pourquoi il manque, quand il manque. */
export function aideRapport(f: FeuilleCaisse): string {
  if (f.reprise && f.total.rapport === null) return REPRISE_SANS_DETAIL
  if (f.zapAttendu) return 'Le rapport ZAP n’est pas encore arrivé : le total attend les bars'
  if (f.accueilMasque) return 'Le guichet de l’Accueil ne se lit pas sans le droit de lire la billetterie'
  if (f.rapportsAttendus > 0) {
    return `En attente du rapport de ${pluriel(f.rapportsAttendus, 'point de vente', 'points de vente')}`
  }
  if (f.total.rapport === null) return 'Rien de repris des rapports pour l’instant'
  return 'Ce que les rapports annoncent sur place, hors offerts'
}

/**
 * Sur quoi repose le chiffre d'affaires du bandeau : l'ARRÊTÉ d'une clôture
 * finalisée, le VALIDÉ d'une nuit validée en bloc, le COMPTÉ, ou les seuls
 * RAPPORTS. `null` quand il n'y a pas de chiffre. La carte de l'accueil n'écrit
 * que les trois premières (`chiffresDeLaCarte`), sans relire l'aide.
 */
export type BaseChiffreAffaires = 'arrete' | 'valide' | 'compte' | 'rapports'

/**
 * LE « CHIFFRE D'AFFAIRES » DU BANDEAU DE LA FICHE DE NUIT — celui qui fait
 * foi, dans cet ordre :
 *
 *   1. une clôture finalisée : son ARRÊTÉ (le retenu prime sur le constaté)
 *      + les préventes — le chiffre versé au mois, pas le compté ;
 *   2. une nuit validée en bloc : le chiffre VALIDÉ, celui du bloc juste
 *      dessous — deux « chiffre d'affaires » différents sur une page, jamais ;
 *   3. sinon, le compté (quand tout l'est) ou les rapports, + préventes,
 *      la base dite dans l'aide.
 */
/**
 * Une validation venue de la reprise de l'historique (script du 16/10/2026) :
 * sa trace de provenance (`history`) porte le libellé, l'onglet et la ligne du
 * suivi de la DAF. Même contrôle que `repriseDe` côté écran
 * (`finance/reprise-historique.ts`), recopié ici parce que ce module reste PUR,
 * sans aucun import.
 */
function estReprise(v: unknown): boolean {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false
  const o = v as Record<string, unknown>
  return typeof o['label'] === 'string' && o['label'] !== '' && typeof o['sheet'] === 'string' && typeof o['row'] === 'number'
}

export function chiffreAffairesDuBandeau(
  f: FeuilleCaisse, validation: { revenue_cents: number | null; history?: unknown } | null,
): { valeur: number | null; aide: string; base: BaseChiffreAffaires | null } {
  if (f.figee) {
    const asso = f.associations.dansLeChiffre ? ', billetterie des associations comprise' : ''
    if (f.ca.arrete !== null && f.preventesArretees !== null) return { valeur: f.ca.arrete, aide: `Arrêté à la finalisation, préventes reversées comprises${asso}`, base: 'arrete' }
    if (f.ca.arrete !== null) return { valeur: f.ca.arrete, aide: `Arrêté à la finalisation + préventes reversées par Shotgun${asso}`, base: 'arrete' }
    return {
      valeur: null,
      aide: f.arreteSurPlace === null ? 'Arrêté à la finalisation, mais ses composantes n’ont pas pu être lues' : 'Arrêté à la finalisation ; les préventes ne sont pas encore connues',
      base: null,
    }
  }
  if (validation !== null) {
    // Le montant validé suit le droit de lire les montants : masqué, il n'y a pas de chiffre.
    const aide = estReprise(validation.history) ? 'Repris de l’historique : Total du suivi de la DAF' : 'Validé sans clôture détaillée'
    return { valeur: validation.revenue_cents, aide, base: validation.revenue_cents === null ? null : 'valide' }
  }
  const plusAsso = f.associations.dansLeChiffre ? ' + billetterie des associations' : ''
  if (f.ca.compte !== null) return { valeur: f.ca.compte, aide: `Compté sur place + préventes reversées par Shotgun${plusAsso}`, base: 'compte' }
  if (f.ca.rapport !== null) return { valeur: f.ca.rapport, aide: `Rapports sur place + préventes reversées par Shotgun${plusAsso}`, base: 'rapports' }
  if (f.zapAttendu || f.rapportsAttendus > 0) return { valeur: null, aide: 'Le rapport ZAP n’est pas encore arrivé : le chiffre attend les bars', base: null }
  if (f.accueilMasque) return { valeur: null, aide: 'Le guichet de l’Accueil ne se lit pas sans le droit de lire la billetterie', base: null }
  return { valeur: null, aide: 'Se calcule quand les préventes sont connues', base: null }
}

/* ═══════════════════════ la carte d'une nuit, à l'accueil ═══════════════════════ */

/**
 * Les bases d'un chiffre d'affaires TAPÉ — compté sur place, arrêté à la
 * finalisation, validé sans clôture détaillée : un chiffre que quelqu'un a
 * saisi ou arrêté. Les seuls rapports n'en sont pas une.
 */
export type BaseChiffreAffairesTape = Exclude<BaseChiffreAffaires, 'rapports'>

/** Ce que la carte écrit devant le chiffre d'affaires tapé. */
export const LIBELLE_CA_CARTE = 'CA'
/** Ce qu'elle écrit à sa place tant que la fiche ne l'estime que d'après les rapports. */
export const CA_PAS_ENCORE_COMPTE = 'pas encore compté'

export interface ChiffresCarte {
  /** Le « Rapport » du bandeau de la fiche ; `null` = la fiche écrit « — ». */
  rapport: number | null
  /** L'aide du « Rapport » de la fiche : pourquoi il manque, quand il manque. */
  aideRapport: string
  /** Le « Chiffre d'affaires » du bandeau de la fiche quand il est TAPÉ ; `null` sinon. */
  ca: { valeur: number; base: BaseChiffreAffairesTape; aide: string } | null
  /**
   * La fiche n'estime son chiffre d'affaires que d'après les rapports : la
   * carte écrit « CA pas encore compté », et ceci en aide. `null` sinon.
   */
  caEnAttente: string | null
}

/**
 * LES DEUX CHIFFRES D'UNE CARTE « LES DERNIÈRES SOIRÉES » (10/10/2026).
 *
 * « Dans l'accueil, on voit le chiffre des rapports et au-dessus, en plus
 * petit, le vrai chiffre d'affaires tapé » (l'exploitant). La carte disait
 * ce que ZAP annonce, bars et vestiaire seulement : 21 857 € le 03/10, quand
 * la fiche de la même nuit disait 26 317 € de « Rapport » (ZAP + Accueil).
 *
 * Ce sont donc EXACTEMENT les chiffres du bandeau de la fiche de nuit : la
 * même feuille (`feuilleDeCaisse`) sur les mêmes entrées — la fiche de nuit
 * (`GET /finance/nights/:date`) et la clôture qu'elle nomme —, puis son
 * « Rapport » (`total.rapport`) et son « Chiffre d'affaires »
 * (`chiffreAffairesDuBandeau`). Aucune formule ici : une seconde règle finirait
 * par contredire la première.
 *
 * Le petit chiffre est le chiffre d'affaires TAPÉ — compté, arrêté ou validé
 * —, et rien d'autre (revue du 11/10/2026). Quand la fiche ne l'estime que
 * d'après les rapports (rapport + préventes), la carte ne recopie pas cette
 * estimation : ce n'est pas le « vrai » chiffre demandé, et un troisième
 * montant au-dessus du rapport se confondrait avec lui. Elle écrit « CA pas
 * encore compté ». Absent pour une autre raison, il se tait — un « — »
 * au-dessus du rapport se lirait comme un chiffre d'affaires nul ou perdu.
 */
export function chiffresDeLaCarte(cloture: ClosingDetail | null, nuit: NuitAvecPreventes): ChiffresCarte {
  const f = feuilleDeCaisse(cloture, nuit)
  const ca = chiffreAffairesDuBandeau(f, nuit.validation)
  const base = ca.base
  return {
    rapport: f.total.rapport,
    aideRapport: aideRapport(f),
    ca: ca.valeur === null || base === null || base === 'rapports' ? null : { valeur: ca.valeur, base, aide: ca.aide },
    caEnAttente: base === 'rapports'
      ? 'La nuit n’est pas encore comptée en entier : la fiche estime son chiffre d’affaires d’après les rapports'
      : null,
  }
}

/* ═══════════════════════ la caisse de la nuit, vue des Soirées ═══════════════════════ */

/** Où en sont les rapports de la nuit. */
export type EtatRapportsNuit =
  /** Le « Rapport » du bandeau existe. */
  | 'RECUS'
  /** Nuit reprise de l'historique : ses rapports ne sont pas dans Gestion, et n'y viendront pas. */
  | 'HORS_GESTION'
  /** Un rapport ZAP (ou celui d'un point de vente ouvert) n'est pas encore arrivé. */
  | 'EN_ATTENTE'
  /** Le guichet de l'Accueil ne se lit pas sans le droit de lire la billetterie. */
  | 'NON_LISIBLE'

/** Où en est le comptage de la nuit. */
export type EtatCompteNuit =
  /** Chaque point de vente ouvert est compté en entier. */
  | 'COMPTE'
  /** Un comptage a commencé : le montant additionne ce qui est compté. */
  | 'EN_COURS'
  /** Rien de compté (ou pas encore de clôture). */
  | 'PAS_ENCORE'
  /** Nuit reprise de l'historique : aucun comptage dans Gestion. */
  | 'HORS_GESTION'

/**
 * LA CAISSE DE LA NUIT — trois chiffres du bandeau de la fiche de nuit, pour
 * les Soirées (15/10/2026) : « Rapport », « Compté », et le chiffre
 * d'affaires quand il est ARRÊTÉ (clôture finalisée) ou VALIDÉ (nuit validée
 * sans clôture détaillée).
 *
 * Aucune formule ici : `feuilleDeCaisse` et `chiffreAffairesDuBandeau`, sur
 * les mêmes entrées que la fiche (la fiche de nuit et la clôture qu'elle
 * nomme, sous les droits de l'acteur). L'estimation « d'après les rapports »
 * n'est PAS un chiffre d'affaires arrêté : elle ne sort pas d'ici.
 */
export interface CaisseDeNuit {
  rapport: number | null
  rapportEtat: EtatRapportsNuit
  /** L'aide du « Rapport » de la fiche (`aideRapport`). */
  aideRapport: string
  compte: number | null
  compteEtat: EtatCompteNuit
  /** L'aide du « Compté » de la fiche (`aideCompte`). */
  aideCompte: string
  /** Le chiffre d'affaires arrêté ou validé ; `null` tant qu'il ne l'est pas. */
  caArrete: { valeur: number; base: 'arrete' | 'valide'; aide: string } | null
}

export function caisseDeLaNuit(cloture: ClotureLue | null, nuit: NuitLue): CaisseDeNuit {
  const f = feuilleDeCaisse(cloture, nuit)
  const ca = chiffreAffairesDuBandeau(f, nuit.validation)
  // L'ordre de `aideRapport` : le ZAP qui manque d'abord, puis le guichet caché.
  const rapportEtat: EtatRapportsNuit = f.total.rapport !== null ? 'RECUS'
    : f.reprise ? 'HORS_GESTION'
      : !f.zapAttendu && f.accueilMasque ? 'NON_LISIBLE'
        : 'EN_ATTENTE'
  const compteEtat: EtatCompteNuit = f.total.compte === null ? (f.reprise ? 'HORS_GESTION' : 'PAS_ENCORE') : f.complet ? 'COMPTE' : 'EN_COURS'
  return {
    rapport: f.total.rapport,
    rapportEtat,
    aideRapport: aideRapport(f),
    compte: f.total.compte,
    compteEtat,
    aideCompte: aideCompte(f),
    caArrete: ca.valeur !== null && (ca.base === 'arrete' || ca.base === 'valide')
      ? { valeur: ca.valeur, base: ca.base, aide: ca.aide }
      : null,
  }
}
